import { forwardRef, type ComponentPropsWithoutRef, type ReactNode } from 'react';
import { useLingui } from '@lingui/react/macro';
import { ArrowUpRight, ShieldCheck, Zap } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogTitle, DialogTrigger } from '@worldcoo/ui/components/dialog';
import { useTranslation } from '@/lib/i18n';
import { Squiggle } from '../primitives';

function AppleLogo(props: { className?: string }) {
	return (
		<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden {...props}>
			<path d="M16.365 1.43c0 1.14-.42 2.22-1.19 3.02-.83.87-2.16 1.54-3.2 1.46-.13-1.1.42-2.27 1.13-3.03.79-.85 2.19-1.47 3.26-1.45zM20.7 17.02c-.53 1.22-.78 1.77-1.46 2.85-.95 1.5-2.29 3.37-3.95 3.38-1.48.02-1.86-.96-3.86-.95-2 .01-2.42.97-3.9.95-1.66-.02-2.93-1.7-3.88-3.2-2.66-4.2-2.94-9.13-1.3-11.75 1.17-1.87 3.01-2.96 4.74-2.96 1.76 0 2.87.97 4.32.97 1.41 0 2.27-.97 4.31-.97 1.54 0 3.17.84 4.33 2.29-3.8 2.08-3.18 7.5.55 9.34z" />
		</svg>
	);
}

function PlayLogo(props: { className?: string }) {
	return (
		<svg viewBox="0 0 24 24" aria-hidden {...props}>
			<path fill="#00d3ff" d="M3.6 2.3 13.4 12 3.6 21.7c-.36-.2-.6-.6-.6-1.1V3.4c0-.5.24-.9.6-1.1z" />
			<path fill="#00f076" d="M13.4 12 3.6 2.3c.13-.07.28-.12.44-.13.3-.03.6.05.86.2L17 8.6 13.4 12z" />
			<path fill="#ffc800" d="M20.5 10.9c.65.37.65 1.32 0 1.7L17 14.6 13.4 12 17 8.6l3.5 2.3z" />
			<path fill="#ff3b30" d="M13.4 12 17 14.6l-6.1 3.5c-.26.15-.56.23-.86.2-.16-.01-.31-.06-.44-.13L13.4 12z" />
		</svg>
	);
}

const StoreBadge = forwardRef<
	HTMLButtonElement,
	ComponentPropsWithoutRef<'button'> & { label: string; logo: ReactNode }
>(function StoreBadge({ label, logo, ...props }, ref) {
	return (
		<button
			ref={ref}
			type="button"
			className="bg-ink flex items-center gap-2.5 rounded-xl px-4 py-2.5 text-white transition-opacity hover:opacity-90"
			{...props}
		>
			{logo}
			<span className="text-base font-semibold">{label}</span>
		</button>
	);
});

function FloatingCard({ icon, text, className }: { icon: ReactNode; text: string; className?: string }) {
	return (
		<div
			className={`absolute flex items-center gap-3 rounded-2xl bg-white px-4 py-3 shadow-[0_10px_30px_0_rgba(0,0,0,0.12)] ${className}`}
		>
			<span className="bg-olive/30 text-ink grid size-9 shrink-0 place-items-center rounded-full">{icon}</span>
			<span className="text-ink max-w-[9rem] text-sm font-medium">{text}</span>
		</div>
	);
}

/** Localized app screenshots; locales without their own capture fall back to EN. */
const APP_MOCKUP_FALLBACK = '/redesign/app-mockup-en.png';
const APP_MOCKUPS: Record<string, string> = {
	ca: '/redesign/app-mockup-ca.png',
	en: APP_MOCKUP_FALLBACK,
	es: '/redesign/app-mockup-es.png',
	fr: '/redesign/app-mockup-fr.png',
	it: '/redesign/app-mockup-it.png',
};

/** App-download band: "Haz seguimiento de tus donaciones en tiempo real" + store badges + phone mockup. */
export function AppDownload() {
	const { t } = useLingui();
	const { language } = useTranslation();
	const mockup = APP_MOCKUPS[language] ?? APP_MOCKUP_FALLBACK;
	return (
		<section className="mx-auto max-w-[1440px] px-6 py-16 md:py-32 xl:px-10">
			<div className="bg-peach grid items-center gap-10 rounded-3xl px-8 py-12 md:px-14 md:py-16 lg:grid-cols-2">
				<div className="flex flex-col gap-6">
					<h2 className="text-ink text-3xl font-medium tracking-tight md:text-5xl">
						{t`Track your donations in`} <Squiggle>{t`real time`}</Squiggle>
					</h2>
					<p className="text-ink/70 max-w-sm text-base md:text-lg">
						{t`Learn the impact of every donation and the status of each project.`}
					</p>
					<Dialog>
						<div className="flex flex-wrap gap-3">
							<DialogTrigger asChild>
								<StoreBadge label="App Store" logo={<AppleLogo className="size-7" />} />
							</DialogTrigger>
							<DialogTrigger asChild>
								<StoreBadge label="Google Play" logo={<PlayLogo className="size-6" />} />
							</DialogTrigger>
						</div>
						<DialogContent className="bg-sand rounded-3xl sm:max-w-md">
							<div className="flex flex-col items-start gap-4 p-2">
								<span className="bg-olive/30 text-ink grid size-12 shrink-0 place-items-center rounded-full">
									<Zap className="size-5 fill-current" />
								</span>
								<DialogTitle className="text-ink text-2xl font-semibold tracking-tight">{t`Coming soon!`}</DialogTitle>
								<DialogDescription className="text-ink/70 text-base">
									{t`Our app will be available on the App Store and Google Play very soon. In the meantime, you can already enjoy it from your browser.`}
								</DialogDescription>
								<a
									href="https://app.worldcoo.com"
									target="_blank"
									rel="noreferrer"
									className="bg-ink mt-2 flex items-center gap-2 rounded-full py-2.5 pr-2.5 pl-5 font-medium text-white transition-opacity hover:opacity-90"
								>
									app.worldcoo.com
									<span className="grid size-6 place-items-center rounded-full bg-white/15">
										<ArrowUpRight className="size-4" />
									</span>
								</a>
							</div>
						</DialogContent>
					</Dialog>
				</div>

				<div className="relative mx-auto h-[26rem] w-full max-w-sm">
					{/* Phone + badges bob together as one unit — the float lives on this shared layer
					    so the cards track the phone. Contained on mobile; on md+ the phone is taller
					    than the box and centered, so it pokes out top and bottom (per Figma). The
					    phone's centering lives on its own inner wrapper, so the two transforms don't clash. */}
					<div className="absolute inset-0 motion-safe:animate-[new-float_6s_ease-in-out_infinite]">
						<div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
							<img src={mockup} alt={t`Worldcoo app preview`} className="h-[26rem] w-auto max-w-none md:h-[40rem]" />
						</div>
						{/* On mobile the card sits just under the amount instead of over the phone's header:
						    the mockup is 587x900 rendered at h-[26rem], so the "223,68 €" figure ends at
						    ~141px down the box and top-36 (144px) clears it. md+ keeps the Figma position. */}
						<FloatingCard
							icon={<Zap className="size-4 fill-current" />}
							text={t`Certify your donation in seconds.`}
							className="top-36 left-0 md:top-40 md:-left-10"
						/>
						<FloatingCard
							icon={<ShieldCheck className="size-4" />}
							text={t`Full transparency of your donation.`}
							className="right-0 bottom-4 md:-right-10 md:bottom-24"
						/>
					</div>
				</div>
			</div>
		</section>
	);
}
